/**
 * Blog-Engine Pipeline-Page (Sprint 1 — Autopilot-Burst MVP).
 *
 * Linke Spalte: 156 ectoin_keyword_seeds gefiltert nach pillar/axis.
 * Pro Seed: Article-Status-Badge + [Autopilot]-Button.
 * Oben Globaler [Autopilot N starten]-Button (Modal: N + research_mode).
 * Live-Refresh alle 5s solange irgendein Article generating ist.
 *
 * Bewusst MINIMAL UI — der eigentliche Content-Workflow ist headless
 * (Backend laeuft, Articles werden published). Diese Page ist v.a.
 * Monitoring + Trigger.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, PlayCircle, RotateCw, Rocket, ExternalLink, FileText, ArrowUp, ArrowDown, ArrowUpDown, Sparkles } from "lucide-react";
import { api } from "../api";
import { Button, StatusBadge, statusLabel } from "../ui";
import { useToast } from "../ui/toast";
import { Modal } from "../ui/modal";

interface PipelineSeedRow {
  seed_id: string;
  pillar: string;
  axis: string;
  keyword: string;
  intent: string | null;
  content_type: string | null;
  ectoin_fit: number | null;
  ai_citation_potential: number | null;
  article_id: string | null;
  article_status: string | null;
  article_published_url: string | null;
  // Server liefert ISO-8601 (COALESCE published_at, updated_at, created_at).
  article_body_at: string | null;
}

interface BatchResult {
  queued_article_ids: string[];
  skipped_seed_ids: string[];
  note?: string | null;
}

const IN_FLIGHT = new Set([
  "queued",
  "researching",
  "toc_building",
  "toc_approved",
  "skeleton_building",
  "generating",
  "publishing",
]);

const BLOCKED_STATES = new Set([
  "compliance_blocked",
  "reviewer_blocked",
  "needs_human_review",
  "error",
]);

type StatusFilter = "all" | "queueable" | "inflight" | "published" | "blocked";
type SortKey = "pillar" | "keyword" | "status" | "url" | "body_at" | null;
type SortDir = "asc" | "desc";

export default function BlogPipelinePage() {
  const toast = useToast();
  const [rows, setRows] = useState<PipelineSeedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pillarFilter, setPillarFilter] = useState<string>("");
  const [axisFilter, setAxisFilter] = useState<string>("");
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchN, setBatchN] = useState(10);
  const [batchMode, setBatchMode] = useState<"sonar" | "deep">("sonar");
  const [batchBusy, setBatchBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  // Sortierung persistieren + Default = neuestes Body-Datum zuerst (desc),
  // leere Datums-Zeilen ans Ende (siehe displayRows). Sonst kamen die Seeds in
  // Server-Reihenfolge (pillar/axis/keyword) → neueste Artikel unsichtbar, und
  // die Sortier-Wahl ging bei jedem Seitenaufruf verloren.
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    const v = localStorage.getItem("sb_blog_sort_key");
    return v === "pillar" || v === "keyword" || v === "status" || v === "url" || v === "body_at"
      ? (v as SortKey)
      : "body_at";
  });
  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const v = localStorage.getItem("sb_blog_sort_dir");
    return v === "asc" || v === "desc" ? (v as SortDir) : "desc";
  });

  useEffect(() => {
    if (sortKey) localStorage.setItem("sb_blog_sort_key", sortKey);
    localStorage.setItem("sb_blog_sort_dir", sortDir);
  }, [sortKey, sortDir]);

  const onCardClick = (next: StatusFilter) => {
    // Click on the active filter toggles back to 'all'.
    setStatusFilter((cur) => (cur === next ? "all" : next));
  };

  const onColClick = (key: SortKey) => {
    if (!key) return;
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const qs: string[] = ["limit=200"];
      if (pillarFilter) qs.push(`pillar=${encodeURIComponent(pillarFilter)}`);
      if (axisFilter) qs.push(`axis=${encodeURIComponent(axisFilter)}`);
      const data = await api.get<PipelineSeedRow[]>(`/api/blog/pipeline?${qs.join("&")}`);
      setRows(data);
    } catch (e: unknown) {
      toast.err("Pipeline laden fehlgeschlagen", String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pillarFilter, axisFilter]);

  // Auto-Refresh waehrend Generation laeuft.
  useEffect(() => {
    const inFlight = rows.some((r) => r.article_status && IN_FLIGHT.has(r.article_status));
    if (!inFlight) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const pillars = useMemo(
    () => Array.from(new Set(rows.map((r) => r.pillar))).sort(),
    [rows],
  );
  const axes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.axis))).sort(),
    [rows],
  );

  const totals = useMemo(() => {
    const t = { total: rows.length, published: 0, inflight: 0, blocked: 0, queueable: 0 };
    for (const r of rows) {
      if (!r.article_status) t.queueable++;
      else if (r.article_status === "published") t.published++;
      else if (IN_FLIGHT.has(r.article_status)) t.inflight++;
      else if (BLOCKED_STATES.has(r.article_status)) t.blocked++;
    }
    return t;
  }, [rows]);

  // Sprint-3 UI: status-filter from clickable stat cards + column sort.
  const displayRows = useMemo(() => {
    let arr = rows;
    if (statusFilter !== "all") {
      arr = arr.filter((r) => {
        const s = r.article_status;
        if (statusFilter === "queueable") return !s;
        if (statusFilter === "inflight") return !!s && IN_FLIGHT.has(s);
        if (statusFilter === "published") return s === "published";
        if (statusFilter === "blocked") return !!s && BLOCKED_STATES.has(s);
        return true;
      });
    }
    if (sortKey) {
      const sign = sortDir === "asc" ? 1 : -1;
      const keyFor = (r: PipelineSeedRow): string => {
        switch (sortKey) {
          case "pillar":
            return `${r.pillar}${r.axis}`;
          case "keyword":
            return r.keyword || "";
          case "status":
            return r.article_status || ""; // leer -> ans Ende (Empty-Last)
          case "url":
            return r.article_published_url ? "1" : "0";
          case "body_at":
            // ACHTUNG, offener Punkt (Task #46): der Vergleich unten ist
            // lexikografisch, und das stimmt fuer diesen Wert NICHT immer.
            // `article_body_at` = COALESCE(published_at, updated_at,
            // created_at) (app/routers/blog.py:343) ueber drei
            // TIMESTAMP(timezone=True)-Spalten (app/models/blog.py:69-80) →
            // das Offset-Suffix ist zwar einheitlich `Z`, ABER Pydantic laesst
            // den Sekundenbruchteil genau dann weg, wenn microseconds == 0:
            // "…T10:00:00Z" vs. "…T10:00:00.500000Z" sortiert falsch herum
            // ('.' < 'Z'). Selten (~1e-6 pro Zeile), aber echt. Der Fix haengt
            // an einem Zeitpunkt-Vergleich statt localeCompare; die eigentliche
            // Arbeit ist, den Comparator fuer einen Dry-Run herauszuziehen.
            // Leer -> ans Ende (Empty-Last).
            return r.article_body_at || "";
          default:
            return "";
        }
      };
      arr = [...arr].sort((a, b) => {
        const ka = keyFor(a);
        const kb = keyFor(b);
        // Leere Werte IMMER ans Ende, unabhaengig von der Sortier-Richtung.
        if (!ka && !kb) return 0;
        if (!ka) return 1;
        if (!kb) return -1;
        return ka.localeCompare(kb) * sign;
      });
    }
    return arr;
  }, [rows, statusFilter, sortKey, sortDir]);

  const startSingle = async (seedId: string, mode: "sonar" | "deep" = "sonar") => {
    try {
      await api.post(`/api/blog/autopilot`, {
        keyword_seed_id: seedId,
        research_mode: mode,
        collection: "blog",
      });
      toast.ok("Autopilot gestartet — Live-Updates folgen.");
      load();
    } catch (e: unknown) {
      toast.err("Autopilot fehlgeschlagen", String(e));
    }
  };

  const startBatch = async () => {
    setBatchBusy(true);
    try {
      const res = await api.post<BatchResult>(`/api/blog/autopilot/batch`, {
        n: batchN,
        pillar: pillarFilter || null,
        axis: axisFilter || null,
        research_mode: batchMode,
        collection: "blog",
      });
      toast.ok(`${res.queued_article_ids.length} Articles gequeued.`);
      setBatchOpen(false);
      load();
    } catch (e: unknown) {
      toast.err("Batch-Autopilot fehlgeschlagen", String(e));
    } finally {
      setBatchBusy(false);
    }
  };

  return (
    <div className="px-4 md:px-6 py-4 max-w-[1400px] mx-auto">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold flex items-center gap-2">
            <Rocket className="w-5 h-5 text-brand-500" /> Blog-Pipeline
          </h1>
          <p className="text-xs text-text-2 mt-0.5">
            {totals.total > 0 ? `${totals.total} Keyword-Ideen · ` : ""}Ziel: bitop.de B2B-Cosmetics-Blog
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/blog/dashboard"
            className="px-2.5 py-1.5 rounded-md border border-border-strong hover:bg-surface-3 text-sm flex items-center gap-1.5"
            title="Blog dashboard (v2 engine)"
          >
            <Sparkles className="w-4 h-4 text-brand-500" /> Dashboard
          </Link>
          <button
            onClick={load}
            disabled={loading}
            className="px-2 py-1.5 rounded-md border border-border-strong hover:bg-surface-3 min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center"
            title="Neu laden"
            aria-label="Pipeline neu laden"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
          </button>
          <Button
            onClick={() => setBatchOpen(true)}
            icon={<Rocket className="w-4 h-4" />}
            className="min-h-11 md:min-h-0"
          >
            Autopilot starten ({batchN} Artikel)
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 text-xs">
        <Stat
          label="Ideen"
          value={totals.total}
          active={statusFilter === "all"}
          onClick={() => onCardClick("all")}
          activeAccent="border-border-strong bg-surface-2"
        />
        <Stat
          label="Startklar"
          value={totals.queueable}
          accent="text-text"
          active={statusFilter === "queueable"}
          onClick={() => onCardClick("queueable")}
          activeAccent="border-border-strong bg-surface-3"
        />
        <Stat
          label="Läuft"
          value={totals.inflight}
          accent="text-accent-700"
          active={statusFilter === "inflight"}
          onClick={() => onCardClick("inflight")}
          activeAccent="border-accent-500 bg-accent-50"
        />
        <Stat
          label="Veröffentlicht"
          value={totals.published}
          accent="text-success"
          active={statusFilter === "published"}
          onClick={() => onCardClick("published")}
          activeAccent="border-success bg-success-soft"
        />
        <Stat
          label="Blockiert"
          value={totals.blocked}
          accent={totals.blocked > 0 ? "text-danger" : "text-text-2"}
          active={statusFilter === "blocked"}
          onClick={() => onCardClick("blocked")}
          activeAccent="border-danger bg-danger-soft"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <select
          value={pillarFilter}
          onChange={(e) => setPillarFilter(e.target.value)}
          aria-label="Pillar-Filter"
          className="px-2 py-1.5 rounded-md border border-border-strong text-sm min-h-11 md:min-h-0"
        >
          <option value="">Alle Pillars</option>
          {pillars.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          value={axisFilter}
          onChange={(e) => setAxisFilter(e.target.value)}
          aria-label="Axis-Filter"
          className="px-2 py-1.5 rounded-md border border-border-strong text-sm min-h-11 md:min-h-0"
        >
          <option value="">Alle Axes</option>
          {axes.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        {statusFilter !== "all" && (
          <button
            onClick={() => setStatusFilter("all")}
            className="text-xs px-2 py-1 rounded-md border border-border-strong text-text-2 hover:bg-surface-3"
            title="Status-Filter zuruecksetzen"
          >
            Status: <span className="font-medium">{statusFilter}</span> ×
          </button>
        )}
      </div>

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-2 text-xs text-text-2">
            <tr>
              <SortableTh
                label="Pillar / Axis"
                colKey="pillar"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={onColClick}
              />
              <SortableTh
                label="Keyword"
                colKey="keyword"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={onColClick}
              />
              <SortableTh
                label="Status"
                colKey="status"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={onColClick}
              />
              <SortableTh
                label="Text"
                colKey="body_at"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={onColClick}
              />
              <SortableTh
                label="URL"
                colKey="url"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={onColClick}
              />
              <th className="text-right px-3 py-2">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="text-center py-6 text-text-3">
                  keine seeds in dieser Auswahl. Filter zuruecksetzen?
                </td>
              </tr>
            )}
            {displayRows.map((r) => (
              <tr key={r.seed_id} className="border-t border-border hover:bg-surface-2/60">
                <td className="px-3 py-2 align-top">
                  <div className="font-medium text-text">{r.pillar}</div>
                  <div className="text-xs text-text-2">{r.axis}</div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="font-medium">{r.keyword}</div>
                  {r.content_type && (
                    <div className="text-[10px] text-text-2 mt-0.5 uppercase tracking-wide">
                      {r.content_type}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  {r.article_status ? (
                    <StatusBadge status={statusLabel(r.article_status).status}>
                      {statusLabel(r.article_status).label}
                    </StatusBadge>
                  ) : (
                    <span className="text-xs text-text-3">Startklar</span>
                  )}
                </td>
                <td className="px-3 py-2 align-top whitespace-nowrap">
                  <BodyDate iso={r.article_body_at} status={r.article_status} />
                </td>
                <td className="px-3 py-2 align-top">
                  {r.article_published_url ? (
                    <a
                      href={r.article_published_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
                    >
                      öffnen <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-xs text-text-3">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.article_id && (
                    <Link
                      to={`/blog/${r.article_id}`}
                      className="text-xs px-2 py-1 rounded-md border border-border-strong text-text hover:bg-surface-3 inline-flex items-center gap-1 min-h-11 md:min-h-0 mr-1"
                      title="Artikel-Detail öffnen"
                    >
                      <FileText className="w-3.5 h-3.5" /> Detail
                    </Link>
                  )}
                  {!r.article_id && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => startSingle(r.seed_id, "sonar")}
                      disabled={!!r.article_status && IN_FLIGHT.has(r.article_status)}
                      icon={<PlayCircle className="w-3.5 h-3.5" />}
                      className="min-h-11 md:min-h-0"
                      title="Einzelnen Artikel per Autopilot starten"
                      aria-label="Single Article Autopilot starten"
                    >
                      Autopilot
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {batchOpen && (
        <BatchModal
          n={batchN}
          setN={setBatchN}
          mode={batchMode}
          setMode={setBatchMode}
          busy={batchBusy}
          onClose={() => setBatchOpen(false)}
          onStart={startBatch}
          pillarFilter={pillarFilter}
          axisFilter={axisFilter}
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  active = false,
  onClick,
  activeAccent,
}: {
  label: string;
  value: number;
  accent?: string;
  active?: boolean;
  onClick?: () => void;
  activeAccent?: string;
}) {
  const base =
    "border rounded-lg px-3 py-2 text-left transition-colors w-full";
  const idle =
    "border-border bg-surface hover:bg-surface-2 hover:border-border-strong";
  const activeClass = activeAccent ?? "border-border-strong bg-surface-2";
  const cls = `${base} ${active ? activeClass : idle}`;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cls}
      >
        <div className="text-[10px] uppercase tracking-wide text-text-2">
          {label}
        </div>
        <div
          className={`text-lg font-semibold tabular-nums ${
            accent ?? "text-text"
          }`}
        >
          {value}
        </div>
      </button>
    );
  }
  return (
    <div className={cls}>
      <div className="text-[10px] uppercase tracking-wide text-text-2">
        {label}
      </div>
      <div
        className={`text-lg font-semibold tabular-nums ${
          accent ?? "text-text"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function BodyDate({
  iso,
  status,
}: {
  iso: string | null;
  status: string | null;
}) {
  if (!iso) {
    return <span className="text-xs text-text-3">—</span>;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return <span className="text-xs text-text-3">—</span>;
  }
  const dateStr = d.toLocaleDateString("de-DE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
  const timeStr = d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const isPublished = status === "published";
  // Locale FEST auf de-DE wie `dateStr`/`timeStr` darüber — ohne Argument war
  // der Tooltip das einzige Element, das im englischen Chrome US-Datum zeigte.
  const titleStr = d.toLocaleString("de-DE");
  return (
    <span
      title={titleStr}
      className={`text-xs tabular-nums ${
        isPublished ? "text-success" : "text-text-2"
      }`}
    >
      {dateStr}{" "}
      <span className="text-[10px] text-text-3">{timeStr}</span>
    </span>
  );
}

function SortableTh({
  label,
  colKey,
  sortKey,
  sortDir,
  onClick,
}: {
  label: string;
  colKey: NonNullable<SortKey>;
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
}) {
  const active = sortKey === colKey;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className="text-left px-3 py-2">
      <button
        type="button"
        onClick={() => onClick(colKey)}
        className={`inline-flex items-center gap-1 hover:text-text ${
          active ? "text-text font-medium" : "text-text-2"
        }`}
        aria-label={`Sortieren nach ${label}`}
        aria-sort={
          active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
        }
      >
        {label}
        <Icon
          className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`}
        />
      </button>
    </th>
  );
}

function BatchModal({
  n,
  setN,
  mode,
  setMode,
  busy,
  onClose,
  onStart,
  pillarFilter,
  axisFilter,
}: {
  n: number;
  setN: (v: number) => void;
  mode: "sonar" | "deep";
  setMode: (v: "sonar" | "deep") => void;
  busy: boolean;
  onClose: () => void;
  onStart: () => void;
  pillarFilter: string;
  axisFilter: string;
}) {
  return (
    <Modal
      onClose={onClose}
      size="sm"
      title="Batch-Autopilot starten"
      icon={<Rocket className="w-4 h-4" />}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={busy}
            className="min-h-11 md:min-h-0"
          >
            Abbrechen
          </Button>
          <Button
            onClick={onStart}
            loading={busy}
            icon={<Rocket className="w-4 h-4" />}
            className="min-h-11 md:min-h-0"
          >
            Start
          </Button>
        </>
      }
    >
        <p className="text-xs text-text-2 mb-3">
          Engine queued die naechsten N seeds (filtered nach pillar/axis falls
          gesetzt) und faehrt jeweils end-to-end: research → TOC → sections →
          polish → compliance → images → Statamic-Publish.
        </p>
        {(pillarFilter || axisFilter) && (
          <div className="text-xs text-text-2 mb-2">
            Filter aktiv: {pillarFilter} {axisFilter && `· ${axisFilter}`}
          </div>
        )}
        <label className="block text-xs font-medium text-text mb-1">
          Anzahl Articles
        </label>
        <input
          type="number"
          min={1}
          max={100}
          value={n}
          onChange={(e) => setN(Math.max(1, Math.min(100, parseInt(e.target.value || "1", 10))))}
          disabled={busy}
          aria-label="Anzahl Articles"
          className="w-full px-3 py-2 rounded-md border border-border bg-surface text-text text-sm min-h-11 md:min-h-0 mb-3"
        />
        <label className="block text-xs font-medium text-text mb-1">
          Research-Mode
        </label>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as "sonar" | "deep")}
          disabled={busy}
          aria-label="Research-Mode"
          className="w-full px-3 py-2 rounded-md border border-border bg-surface text-text text-sm min-h-11 md:min-h-0"
        >
          <option value="sonar">Sonar (default, schnell, billig)</option>
          <option value="deep">Deep (sonar-pro, langsamer, teurer)</option>
        </select>
    </Modal>
  );
}
